import { requireExecutionPhaseId } from "../../execution/executionJobPhaseInvariant";
import type {
  CyanDelegationAuthority,
  WorkPackage,
} from "./agentExecutionContract";

export interface PersistentExecutionQueue {
  enqueue(input: {
    executionId: string;
    workItemId: string;
    phaseId: string;
    department: string;
    agentRole: string;
    payload: unknown;
  }): Promise<void>;
}

export interface ExecutionIdFactory {
  createExecutionId(): string;
}

export interface ExecutionLinkAuthority {
  save(input: {
    executionJobId: string;
    executionId: string;
    programId: string;
    workItemId: string;
    department: string;
    agentId: string;
    createdAt: string;
  }): Promise<void>;
}

export class ExecutionQueueDelegation implements CyanDelegationAuthority {
  constructor(
    private readonly queue: PersistentExecutionQueue,
    private readonly ids: ExecutionIdFactory,
    private readonly links?: ExecutionLinkAuthority,
  ) {}

  async dispatch(workPackage: WorkPackage) {
    const phaseId = requireExecutionPhaseId(workPackage);
    const executionId = this.ids.createExecutionId();

    await this.queue.enqueue({
      executionId,
      workItemId: workPackage.id,
      phaseId: phaseId,
      department: workPackage.department,
      agentRole: workPackage.agentRole,
      payload: workPackage,
    });

    /*
     * The execution job and orchestration program must remain connected.
     *
     * The persistent worker resolves execution context through
     * cyan_execution_links before it invokes the assigned agent.
     */
    if (this.links) {
      const context = workPackage as WorkPackage & {
        programId?: string;
        assignedAgentId?: string;
      };

      if (!context.programId || !context.assignedAgentId) {
        throw new Error(
          "Execution link requires programId and assignedAgentId.",
        );
      }

      await this.links.save({
        executionJobId: executionId,
        executionId,
        programId: context.programId,
        workItemId: workPackage.id,
        department: workPackage.department,
        agentId: context.assignedAgentId,
        createdAt: new Date().toISOString(),
      });
    }

    return {
      executionId,
      workItemId: workPackage.id,
    };
  }
}
